import {
  mergeAttributes,
  type CommandProps,
  type Editor,
  type NodeViewRendererProps,
} from "@tiptap/core";
// tiptap v3 removed this package's default export; Table is named-only (#11904).
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import getTableAsString from "./getTableAsString";
// Routed through @tiptap/pm rather than the bare prosemirror-* packages
// (#11904): those are not declared dependencies and resolve only by hoisting,
// so a second ProseMirror copy in the tree would make every PluginKey built
// here an object the editor never matches.
import { keymap } from "@tiptap/pm/keymap";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { NodeSelection } from "@tiptap/pm/state";
import type { Selection } from "@tiptap/pm/state";
import { CellSelection } from "@tiptap/pm/tables";

const styles = {
  tableContainer: "tiptap-table-container",
  tableWrapper: "tiptap-table-wrapper",
  headerRow: "tiptap-header-row",
  row: "tiptap-row",
  copyButton: "tiptap-copy-button",
};

const copyIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>`;

const injectStyles = () => {
  /* A report's table carries the same anatomy the chat answer tables do
     (#11426): tinted header band with uppercase micro headers, hairline row
     rules, no zebra, the UI face, figures in ink. The Copy control sits
     BELOW the table as a quiet text control — a control bar on
     top of a table reads as a claim about what follows. Selectors are
     prefixed with .ProseMirror where the editor's base table CSS
     (tiptap-editor.css) would otherwise win on specificity. */
  const css = `
    .${styles.tableContainer} {
      position: relative;
      /* A block table flows in the prose: paragraph-like spacing above,
         room below for the quiet Copy control (CD note, Reports round). */
      margin-top: 16px;
      padding-bottom: 34px;
      overflow-x: auto;
    }

    .${styles.copyButton} {
      position: absolute;
      bottom: 5px;
      left: 0;
      background: none;
      border: none;
      padding: 4px 8px 4px 0;
      font-size: var(--fp-t-fine);
      font-weight: 510;
      color: var(--fp-ink-4);
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 5px;
      height: 24px;
      width: fit-content;
    }

    .${styles.copyButton}:hover {
      color: var(--fp-ink);
    }

    .${styles.copyButton} svg {
      width: 13px;
      height: 13px;
    }

    /* The table spans the document measure (CD, Reports round): a
       shrink-wrapped table reads as a chip stack, not a table. */
    .ProseMirror .${styles.tableWrapper},
    .${styles.tableWrapper} {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      border: 1px solid var(--fp-border);
      border-radius: 10px;
      overflow: hidden;
      background: var(--fp-surface);
      margin: 0;
    }

    .ProseMirror .${styles.tableWrapper} td,
    .ProseMirror .${styles.tableWrapper} th,
    .${styles.tableWrapper} td,
    .${styles.tableWrapper} th {
      border: none;
      border-bottom: 1px solid var(--fp-border);
      padding: 9px 14px;
      text-align: left;
      font-family: var(--fp-font-ui);
      color: var(--fp-ink-2);
    }

    .${styles.tableWrapper} th *,
    .${styles.tableWrapper} td * {
      font-size: 12.5px !important;
      font-family: inherit;
    }

    .ProseMirror .${styles.tableWrapper} .${styles.headerRow} th,
    .${styles.headerRow} th {
      background-color: var(--fp-band);
      color: var(--fp-ink-3);
      font-weight: 560;
      text-transform: uppercase;
      letter-spacing: var(--fp-track-caps);
      border-bottom: 1px solid var(--fp-border-strong);
    }

    .${styles.headerRow} th * {
      font-size: var(--fp-t-micro) !important;
      text-transform: uppercase;
    }

    /* No zebra: every body row sits on the surface. */
    .ProseMirror .${styles.tableWrapper} .${styles.row},
    .${styles.row},
    .${styles.row}:nth-child(even) {
      background-color: var(--fp-surface);
    }

    .${styles.tableWrapper} tr:last-child td {
      border-bottom: none;
    }

    /* The record column reads at the primary level. */
    .ProseMirror .${styles.tableWrapper} .${styles.row} td:first-child,
    .${styles.row} td:first-child {
      color: var(--fp-ink);
      font-weight: 510;
    }


    /* Table cell selection styles - TipTap adds .selectedCell class */
    /* Light blue background, no border */
    .${styles.tableWrapper} .selectedCell {
      background-color: #dbeafe !important;
    }

    /* Ensure selected cells are visible even in header rows */
    .${styles.headerRow} .selectedCell {
      background-color: #bfdbfe !important;
    }

    /* Table node selection - when entire table is selected */
    .ProseMirror-selectednode .${styles.tableWrapper} {
      outline: 2px solid #93c5fd;
      outline-offset: 2px;
    }

    /* ProseMirror cell selection highlighting */
    .ProseMirror .${styles.tableWrapper} td[class*="selectedCell"],
    .ProseMirror .${styles.tableWrapper} th[class*="selectedCell"] {
      background-color: #dbeafe !important;
    }
  `;

  const existingStyle = document.getElementById("tiptap-table-styles");
  if (existingStyle) {
    existingStyle.remove();
  }

  const styleElement = document.createElement("style");
  styleElement.id = "tiptap-table-styles";
  styleElement.textContent = css;
  document.head.appendChild(styleElement);
};

async function copyTableToClipboard(tableElement: HTMLTableElement) {
  const csvContent = getTableAsString(tableElement);
  try {
    await navigator.clipboard.writeText(csvContent);
    (window as Window).openFpToast("Table copied to clipboard", "success");
    return true;
  } catch (err) {
    console.error("Failed to copy table:", err);
    return false;
  }
}
interface SelectedCellBounds {
  /** Row index of the topmost selected cell within its table. */
  startRow: number;
  /** Row index of the bottommost selected cell. */
  endRow: number;
  /** Column index of the leftmost selected cell within its row. */
  startCol: number;
  /** Column index of the rightmost selected cell. */
  endCol: number;
  /** Rows in the table the selection sits in. */
  totalRows: number;
  /** Cells in that table's first row. */
  totalCols: number;
}

/**
 * The rectangle a cell selection covers inside its table, or null when the
 * selection is not a cell selection or is not inside a table.
 *
 * Written for #11904 to replace five near-copies that each detected a cell
 * selection by `selection.constructor.name === "CellSelection"` (or by a
 * property prosemirror-tables does not define). A constructor name is a
 * minifier's to rename, so under a differently built v3 bundle Backspace over
 * a fully selected table would quietly stop deleting it. `instanceof
 * CellSelection` is stable, and being a type guard it also removes the `as
 * any` casts the copies needed to reach `$anchorCell` / `$headCell`.
 */
function selectedCellBounds(selection: Selection): SelectedCellBounds | null {
  if (!(selection instanceof CellSelection)) {
    return null;
  }

  const { $anchorCell, $headCell } = selection;

  let tableNode: ProseMirrorNode | null = null;
  let tableDepth = -1;
  for (let depth = $anchorCell.depth; depth > 0; depth--) {
    const node = $anchorCell.node(depth);
    if (node.type.name === "table") {
      tableNode = node;
      tableDepth = depth;
      break;
    }
  }
  if (!tableNode || tableDepth === -1) {
    return null;
  }

  const firstRow = tableNode.firstChild;
  if (!firstRow) {
    return null;
  }

  // A cell position resolves with the ROW as its parent, so `index()` is the
  // cell's column and `index(tableDepth)` its row.
  const anchorRow = $anchorCell.index(tableDepth);
  const headRow = $headCell.index(tableDepth);
  const anchorCol = $anchorCell.index();
  const headCol = $headCell.index();

  return {
    startRow: Math.min(anchorRow, headRow),
    endRow: Math.max(anchorRow, headRow),
    startCol: Math.min(anchorCol, headCol),
    endCol: Math.max(anchorCol, headCol),
    totalRows: tableNode.childCount,
    totalCols: firstRow.childCount,
  };
}

/** Whether the selection spans every cell of its table. */
function coversWholeTable(bounds: SelectedCellBounds): boolean {
  return (
    bounds.startRow === 0 &&
    bounds.endRow === bounds.totalRows - 1 &&
    bounds.startCol === 0 &&
    bounds.endCol === bounds.totalCols - 1
  );
}

/**
 * Delete the table when the whole of it is selected, so Backspace removes the
 * table rather than emptying its cells and leaving an empty grid behind.
 * Returns false — letting the default handler run — for any other selection.
 */
function deleteWhollySelectedTable(editor: Editor): boolean {
  const { selection } = editor.state;

  const bounds = selectedCellBounds(selection);
  if (bounds && coversWholeTable(bounds)) {
    return editor.chain().focus().deleteTable().run();
  }

  // The table can also be selected as a whole node, e.g. by clicking it
  // rather than dragging across its cells.
  if (
    selection instanceof NodeSelection &&
    selection.node.type.name === "table"
  ) {
    return editor.chain().focus().deleteTable().run();
  }

  return false;
}

const CustomTable = Table.extend({
  addOptions() {
    // `this.parent` is optional in the type even though Table always defines
    // addOptions, so spreading it alone yields all-optional fields and no
    // longer satisfies TableOptions under the v3 typings (#11904). Falling
    // back to the extension's own defaults keeps the result complete without
    // a cast.
    const parentOptions = this.parent?.() ?? Table.options;
    return {
      ...parentOptions,
      allowTableNodeSelection: true,
    };
  },

  // The answer a table came from, read by the cross-workspace copy path
  // (`EditorMain.updateQIDOfQueryInputs`, #11312).
  addAttributes() {
    return {
      ...this.parent?.(),
      qid: {
        default: null,
      },
    };
  },

  addKeyboardShortcuts() {
    const parentShortcuts = this.parent?.() || {};

    return {
      ...parentShortcuts,
      // One handler for both keys. These used to be two near-copies of the
      // same hundred-line bounds calculation, and the copies had drifted:
      // Delete read a cell's index within its row as its ROW index, so it
      // only agreed with Backspace on a square selection (#11904).
      Backspace: ({ editor }) => deleteWhollySelectedTable(editor),
      Delete: ({ editor }) => deleteWhollySelectedTable(editor),
    };
  },
  addCommands() {
    return {
      ...this.parent?.(),
      insertTable:
        (options?: { rows?: number; cols?: number; withHeaderRow?: boolean }) =>
        (props: CommandProps) => {
          // Prevent inserting a table inside a table cell
          const { state } = props;
          const { selection } = state;
          const $anchor = selection.$anchor;

          // Check if we're inside a table cell or header
          let depth = $anchor.depth;
          while (depth > 0) {
            const node = $anchor.node(depth);
            if (
              node.type.name === "tableCell" ||
              node.type.name === "tableHeader"
            ) {
              // We're inside a table cell, don't allow insertion
              return false;
            }
            depth--;
          }

          // If we're not in a table cell, proceed with the original command
          const parentCommands = this.parent?.();
          if (parentCommands?.insertTable) {
            return parentCommands.insertTable(options)(props);
          }
          return false;
        },
    };
  },

  addProseMirrorPlugins() {
    const parentPlugins = this.parent?.() || [];

    // Store editor reference for use in keymap
    const editor = this.editor;

    // Create a keymap plugin to handle Backspace/Delete for table deletion
    // A keymap plugin as well as the extension's own Backspace/Delete
    // shortcuts above: this one is a ProseMirror plugin, so it is consulted
    // before other extensions' handlers get to empty the cells one by one.
    const tableDeleteKeymap = keymap({
      // The leading parameter is ProseMirror's Command signature, which this
      // handler does not control; the state the helper needs is the editor's.
      Backspace: (_state, dispatch) =>
        dispatch ? deleteWhollySelectedTable(editor) : false,
      Delete: (_state, dispatch) =>
        dispatch ? deleteWhollySelectedTable(editor) : false,
    });

    // No selection-highlight plugin: prosemirror-tables' own tableEditing()
    // (registered by Table) already decorates every selected cell with
    // `selectedCell`, which the stylesheet styles. A hand-rolled copy lived
    // here for years behind a check that never passed; making it live under
    // #11904 showed it looked up the FIRST table in the document, so on a
    // report with two tables it stripped the correct highlight and painted
    // the wrong one. Deleted rather than revived.
    return [...parentPlugins, tableDeleteKeymap];
  },

  addNodeView() {
    return ({ HTMLAttributes }: NodeViewRendererProps) => {
      const container = document.createElement("div");
      container.className = styles.tableContainer + " custom-scrollbar";

      const copyButton = document.createElement("button");
      copyButton.className = styles.copyButton;

      copyButton.innerHTML = `${copyIcon} Copy`;

      copyButton.addEventListener("click", (e) => {
        e.stopPropagation();
        const table = container.querySelector("table");
        if (table) {
          copyTableToClipboard(table);
        }
      });

      const table = document.createElement("table");
      table.className = styles.tableWrapper;

      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        if (key !== "class") {
          table.setAttribute(key, String(value));
        }
      });

      container.appendChild(copyButton);
      container.appendChild(table);

      return {
        dom: container,
        contentDOM: table,

        destroy: () => {
          container.remove();
        },
      };
    };
  },
});

const CustomTableRow = TableRow.extend({
  renderHTML({ HTMLAttributes, node }) {
    // console.log("Row HTMLAttributes", HTMLAttributes);
    const isHeader = node.firstChild?.type.name === "tableHeader";
    return [
      "tr",
      mergeAttributes(HTMLAttributes, {
        class: isHeader ? styles.headerRow : styles.row,
      }),
      0,
    ];
  },
});

const CustomTableCell = TableCell.extend({
  addKeyboardShortcuts() {
    return {
      ...this.parent?.(),
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ["td", mergeAttributes(HTMLAttributes), 0];
  },
});

if (typeof window !== "undefined") {
  injectStyles();
}

export const tableExtensions = [
  CustomTable.configure({
    resizable: true,
  }),
  CustomTableRow,
  CustomTableCell,
  TableHeader,
];
